import { useState, useEffect, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import Button from "@/components/ui/Button";
import Comment from "@/components/comments/Comment";
import CommentMentionsInput from "@/components/comments/CommentMentionsInput";

export default function CommentGroup({
  data,
  onDelete,
  // Highlight support (URL ?hc= param)
  highlightCommentId,
  // Permission props
  canReply = true,
  canEdit = false,
  canDelete = true,
  canResolve = true,
  onUpdate,
}) {
  const [comment, setComment] = useState(data);
  const [updating, setUpdating] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const replyRef = useRef(null);

  const getData = async () => {
    try {
      const response = await apiFetch(
        `/api/shows/${comment.show}/comments/${comment._id}`,
      );
      if (!response.ok) throw new Error("Failed to fetch comment");
      const newComment = await response.json();
      setComment(newComment);
    } catch (error) {
      console.error("Error fetching comment:", error);
    }
  };

  const handleSubmitReply = async () => {
    try {
      setUpdating(true);
      const response = await apiFetch(`/api/shows/${comment.show}/comments`, {
        method: "POST",
        body: JSON.stringify({
          text: replyText,
          uid: comment._id,
          element_type: "comment",
          episode: comment.episode,
          segment: comment.segment,
          ...(comment.scene ? { scene: comment.scene } : {}),
        }),
      });
      if (!response.ok) throw new Error("Failed to reply to comment");
      await getData();
      setReplying(false);
      setReplyText("");
    } catch (error) {
      console.error("Error replying to comment:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSubmitReply();
    } else if (e.key === "Escape") {
      setReplying(false);
      setReplyText("");
    }
  };

  useEffect(() => {
    if (replying && replyRef?.current) {
      setTimeout(() => replyRef.current.focus(), 100);
    }
  }, [replying]);

  useEffect(() => {
    if (
      highlightCommentId &&
      comment.replies?.some((r) => r._id === highlightCommentId)
    ) {
      setExpanded(true);
    }
  }, [comment, highlightCommentId]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col border rounded-tr-xl rounded-bl-xl rounded-br-xl rounded-tl-sm relative">
        <Comment
          data={comment}
          canResolve={canResolve}
          canEdit={canEdit}
          canDelete={canDelete}
          onDelete={onDelete}
          onReply={() => {
            setReplying(true);
            setExpanded(true);
          }}
          onUpdate={onUpdate}
          highlightCommentId={highlightCommentId}
        />

        {comment?.replies?.length > 0 && (
          <div className="flex flex-col gap-1">
            <div
              className="text-secondary-foreground/20 hover:text-secondary-foreground/60 text-[10px] flex items-center justify-between px-2 py-1 bg-secondary/30 cursor-pointer"
              onClick={() => setExpanded(!expanded)}>
              <div className="flex items-center gap-1">
                <span className="text-secondary-foreground/60">Replies</span>
                <span className="text-secondary-foreground/60 bg-secondary rounded-full px-[2px] py-[1px] min-w-[20px] text-center">
                  {comment.replies.length}
                </span>
              </div>
              {expanded ? (
                <FiChevronUp className="w-4 h-4" />
              ) : (
                <FiChevronDown className="w-4 h-4" />
              )}
            </div>
            {expanded && (
              <div className="flex flex-col gap-1">
                {comment.replies.map((reply) => (
                  <Comment
                    key={reply._id}
                    data={reply}
                    canResolve={false}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onDelete={getData}
                    onReply={() => setReplying(true)}
                    highlightCommentId={highlightCommentId}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {replying && (
          <div className="flex flex-col gap-1 p-1">
            <CommentMentionsInput
              value={replyText}
              onChange={setReplyText}
              onKeyDown={handleReplyKeyDown}
              placeholder="Add reply..."
              inputRef={replyRef}
            />
            <div className="flex items-center gap-1 justify-end">
              <Button
                type="danger-ghost"
                size="xs"
                onClick={() => {
                  setReplying(false);
                  setReplyText("");
                }}>
                Cancel
              </Button>
              <Button type="confirm" size="xs" onClick={handleSubmitReply}>
                Reply
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
